import React from 'react';
import NumberFormat from 'react-number-format';
import Select from '../../commonComponents/util/Select';
import RadioGroup from '../../commonComponents/util/RadioGroup';
import ValidatedInput from '../../commonComponents/util/ValidatedInput';
import ValidatedPhoneInput from '../../commonComponents/util/ValidatedPhoneInput';
import AddressAutocomplete from '../../commonComponents/shared/AddressAutocomplete';
import FadeOutErrorMessage from '../../commonComponents/util/FadeOutErrorMessage';
import LoadingSpinner from '../../commonComponents/util/LoadingSpinner';
import AutocompleteMultiInput from '../../commonComponents/util/AutocompleteMultiInput';
import RecipientReferencesTable from '../../applicationsComponents/applicationsCommon/RecipientReferencesTable';
import ApplicantPledgesTable from '../../applicationsComponents/applicationsCommon/ApplicantPledgesTable';

import IncompleteApplicationWarning from '../../commonComponents/shared/IncompleteApplicationWarning';
import BankAccountsTable from './RecipientBankAccountsTable';
import AchAccountsTable from './AchAccountsTable';
import { OrgEmploymentTypes } from '../../../lib/systemVariables';
import _cloneDeep from 'lodash.clonedeep';

import { RecipientTypes, ApplicationStatuses } from '../../../lib/systemVariables';
import { deepEquals, isValidEmail, toTitleCase, validateFieldLength } from '../../../lib/utils';

export default class RecipientDetailsEditCard extends React.Component {
    constructor(props) {
        super(props);

        const { recipient } = this.props;
        const hasPrevAddress = recipient.previousAddress.streetNumber
            || recipient.previousAddress.street
            || recipient.previousAddress.city
            || recipient.previousAddress.state
            || recipient.previousAddress.zip
            || recipient.previousAddress.country
            || recipient.previousAddress.unitType
            || recipient.previousAddress.apartment;

        this.initialState = {
            recipient: props.getInitialFormattedRecipient(recipient),
            submitAttempted: recipient.incomplete,
            showEmail: !!(recipient.email || recipient.applicantType === RecipientTypes.Organization),
            showSecondPhone: !!recipient.secondPhoneNumber,
            showPrevAddress: hasPrevAddress,
            setTrustedPayee: !!recipient.trustedPayee,
            error: false,
            dataErrors: {
                phoneNumber: false,
                email: false,
                secondPhoneNumber: false,
                taxId: false,
            }
        };

        this.state = _cloneDeep(this.initialState);

        this.updateRecipient = this.updateRecipient.bind(this);
        this.updateOrganization = this.updateOrganization.bind(this);
        this.onChange = this.onChange.bind(this);
        this.updateDataErrors = this.updateDataErrors.bind(this);
        this.save = this.save.bind(this);
    }

    updateRecipient(name, value) {
        if (name === "firstName" || name === "lastName" || name === "reportName") value = toTitleCase(value);

        this.setState({ recipient: { ...this.state.recipient, [name]: value } });
    }

    updateOrganization(name, value) {
        if (name === 'registered') {
            value ? this.setState({
                recipient: {
                    ...this.state.recipient,
                    organization: {
                        ...this.state.recipient.organization,
                        taxId: '',
                        [name]: !value
                    }
                }
            }) : this.setState({
                recipient: {
                    ...this.state.recipient,
                    organization: {
                        ...this.state.recipient.organization,
                        [name]: !value
                    }
                }
            });
            return;
        }

        if (name === "name") value = toTitleCase(value);

        this.setState({
            recipient: {
                ...this.state.recipient,
                organization: { ...this.state.recipient.organization, [name]: value }
            }
        });
    }

    onChange(name, value) {
        this.setState({ [name]: value });
    }

    validateEmail(name, value) {
        const isValid = isValidEmail(value);
        this.updateDataErrors(name, !isValid);
        return isValid;
    }

    validateLength(name, length, value) {
        const isValid = validateFieldLength(length, value);
        this.updateDataErrors(name, !isValid);
        return isValid;
    }

    updateDataErrors(name, value) {
        this.setState({ dataErrors: { ...this.state.dataErrors, [name]: value } });
    }

    async save() {
        if (this.state.error) this.setState({ error: false }); //remove prev error

        const incompleteFields = this.props.getIncompleteFields(this.state.recipient, this.state.setTrustedPayee);
        if (this.state.submitAttempted || !incompleteFields) {
            const recipient = {
                ...this.state.recipient,
                references: this.state.recipient.references.map(ref => ({ referenceId: ref.referenceId, relationship: ref.relationship })),
                solicitors: this.state.recipient.solicitors.map(ref => ({ referenceId: ref.referenceId, relationship: ref.relationship })),
                roshYeshivas: this.state.recipient.roshYeshivas.map(ref => ({ referenceId: ref.referenceId, relationship: ref.relationship })),
                trustedPayeeId: this.state.recipient.trustedPayee ? this.state.recipient.trustedPayee.referenceId : null
            };
            await this.props.updateRecipient(this.props.recipient.applicantId, recipient);

            if (this.props.updateRecipientDetails.success) {
                if (this.props.recipient.incomplete && (!incompleteFields || incompleteFields.recipientInfo.every(field => field.indexOf('Bank Account') >= 0)))
                    this.props.getApplicationHistory(); //reload applications to refelect recipient status change (if recip is complete, or just missing bank info for applications not paid to recipient)

                this.props.getRecipientAccountDetails(this.props.recipient.applicantId, true);
                this.props.toggleEditMode();

                if (this.state.recipient.address &&
                    ((this.state.recipient.address.country && this.props.tags.nationalities.indexOf(this.state.recipient.address.country) < 0)
                        || (this.state.recipient.address.city && this.props.tags.cities.indexOf(this.state.recipient.address.city) < 0))) {
                    this.props.getSystemSettings(); //get updated nationalities/cities
                }

            } else {
                this.setState({ error: true });
            }
        } else {
            if (!this.state.submitAttempted) this.setState({ submitAttempted: true });
        }
    }

    allowIncompleteUpdate = (incompleteFields) => {
        if (this.props.recipient.incomplete) { //original recipient was incomplete

            const origIncompleteFields = this.props.getIncompleteFields(this.initialState.recipient);
            const noNewIncompleteFields = incompleteFields.recipientInfo.every(field => origIncompleteFields.recipientInfo.indexOf(field) >= 0);
            //all incomplete required fields in updated recipient were originally incomplete as well - making it 'more / = complete', not less           

            if (noNewIncompleteFields) return true;

            const isTotallyIncomplete = (this.props.recipient.incomplete //recipient is incomplete
                //AND all recipient applications are incomplete
                && (this.props.applicationHistory.every(app => app.status === ApplicationStatuses.Incomplete) 
                //OR only missing payment info - to accomodate for the fact that recipeints can be missing payment info and thus be incomplete but have complete applications because they alernate complete payment info
                    || (incompleteFields && incompleteFields.recipientInfo.every(field => field.indexOf('Bank Account') >= 0))) 
            );
            //this logic is currently live but not technically 100% accurate.
            //if only missing bank info allow to update even if has complete applications - truth is applications may NOT have their own payment info...  
            //solution would be to get flag from backend to determine if 'applicationsRelyOnPaymentInfo'...

            return isTotallyIncomplete;

            //TODO:
            //shud allow update if only removed bank info and NO apps paid to recipient...
        }

        return false;
    }

    onChangeParticipant = (name, value) => {

        const recipient = {
            ...this.state.recipient,
            [name]: value
        };

       if (name === 'orgEmploymentType') {
            if (value !== OrgEmploymentTypes.Other) {
                recipient.orgEmploymentPosition = "";
            } else if (value !== OrgEmploymentTypes.Rebbi) {
                recipient.orgEmploymentGrade = "";
                recipient.orgEmploymentSeder = "";
            }
        } 
        
        if (name === 'orgEmploymentGrade' && value < 9 && recipient.orgEmploymentSeder) {
            recipient.orgEmploymentSeder = "";
        }

        this.setState({recipient});    
    }

    render() {
        const { dataErrors, recipient, showPrevAddress, submitAttempted } = this.state;
        const incompleteFields = this.props.getIncompleteFields(recipient, this.state.setTrustedPayee);
        const allowIncomplete = incompleteFields ? this.allowIncompleteUpdate(incompleteFields) : false;
        // previous allowIncomplete logic:
        //  (this.props.recipient.incomplete //recipient is incomplete
        //     && (this.props.applicationHistory.every(app => app.status === ApplicationStatuses.Incomplete) //AND all recipient applications are incomplete
        //         || (incompleteFields && incompleteFields.recipientInfo.every(field => field.indexOf('Bank Account') >= 0))) //OR only missing payment info - to accomodate for the fact that recipeints can be missing payment info and thus be incomplete but have complete applications because they alernate complete payment info
        // );

        const isDisabled =
            (this.state.submitAttempted && incompleteFields && !allowIncomplete)
            || (deepEquals(this.initialState.recipient, this.state.recipient))
            || (this.props.recipient.incomplete && !recipient.address.street && this.props.recipient.addressUsedForThirdParty)
            || ((recipient.applicantType === RecipientTypes.Individual && (!recipient.firstName || !recipient.lastName))
                || (recipient.applicantType === RecipientTypes.Organization && !recipient.organization.name));

        let disabledMessage;
        if (isDisabled &&
            ((recipient.applicantType === RecipientTypes.Individual && (!recipient.firstName || !recipient.lastName))
                || (recipient.applicantType === RecipientTypes.Organization && !recipient.organization.name)))
            disabledMessage = "Recipient name must be complete."

        return (
            <div className="card application-details-info">
                <div className="application-details-info-edit">
                    {recipient.applicantType === RecipientTypes.Individual &&
                        <div className="info-row flex-justify">
                            <div className="title-select">
                                <label className="input-label">Title</label>
                                <Select clearable={true}
                                    options={recipient.isIsraeli ? this.props.tags.israelPersonTitles : this.props.tags.usPersonTitles}
                                    label="Title" name="personTitleId"
                                    value={recipient.personTitleId}
                                    onChange={(e) => this.updateRecipient(e.target.name, e.target.value)} />
                            </div>
                            <ValidatedInput input={<input type="text" />}
                                label="First Name" name="firstName" value={recipient.firstName}
                                onChange={this.updateRecipient}
                                required={recipient.applicantType === "Individual"}
                                showRequiredError={submitAttempted} />
                            <ValidatedInput input={<input type="text" />}
                                label="Last Name" name="lastName" value={recipient.lastName}
                                onChange={this.updateRecipient}
                                required={recipient.applicantType === RecipientTypes.Individual}
                                showRequiredError={submitAttempted} />
                            <div>
                                <label className="input-label">Report Name</label>
                                <input type="text" value={recipient.reportName}
                                    onChange={(e) => this.updateRecipient('reportName', e.target.value)} />
                            </div>
                        </div>}
                    {recipient.applicantType === RecipientTypes.Organization &&
                        <div className="info-row flex-center">
                            <div className="column-quarter">
                                <label className="input-label">Institution Title</label>
                                <Select clearable={true}
                                    options={recipient.isIsraeli ? this.props.tags.israelInstitutionTitles : this.props.tags.usInstitutionTitles}
                                    label="Institution Title" name="institutionTitleId"
                                    value={recipient.organization.institutionTitleId}
                                    onChange={(e) => this.updateOrganization(e.target.name, e.target.value)} />
                            </div>
                            <div className="column-quarter">
                                <ValidatedInput input={<input type="text" />}
                                    label="Name" name="name" value={recipient.organization.name}
                                    onChange={this.updateOrganization}
                                    required={recipient.applicantType === RecipientTypes.Organization}
                                    showRequiredError={submitAttempted} />
                            </div>
                            <div className="column-quarter">
                                <label className="input-label">Report Name</label>
                                <input type="text" value={recipient.reportName}
                                    onChange={(e) => this.updateRecipient('reportName', e.target.value)} />
                            </div>
                            <div className="column-quarter">
                                <ValidatedInput input={<NumberFormat placeholder="#########" format="#########"
                                    onValueChange={(values) => this.updateOrganization('taxId', values.value)}
                                    disabled={!recipient.organization.registered} />}
                                    label="Tax ID" name="taxId" value={recipient.organization.taxId}
                                    errorMessage="Invalid Tax ID"
                                    validated={true} showValidationError={true}
                                    validate={this.validateLength.bind(this, 'taxId', 9)}
                                    required={true}
                                    showRequiredError={recipient.applicantType === RecipientTypes.Organization && recipient.organization.registered && submitAttempted} />
                            </div>
                            <div className="column-quarter">
                                <div className="custom-check-container">
                                    <input type="checkbox" id="check" className="custom-check" name="registered"
                                        checked={!recipient.organization.registered}
                                        onChange={(e) => this.updateOrganization(e.target.name, e.target.checked)} />
                                    <label htmlFor="check">Not Registered</label>
                                </div>
                            </div>
                        </div>}
                    <div className="info-row">
                        <div className="column-third">
                            <ValidatedPhoneInput
                                span1={!this.state.showSecondPhone &&
                                    <span onClick={this.onChange.bind(this, 'showSecondPhone', true)}
                                        className="link-text small-text">Add Phone</span>}
                                span2={(!this.state.showEmail && recipient.applicantType === RecipientTypes.Individual) &&
                                    <span onClick={this.onChange.bind(this, 'showEmail', true)}
                                        className="link-text small-text">Add Email</span>}
                                phoneNumber={recipient.phoneNumber}
                                phoneType={recipient.phoneType}
                                phoneNumberName="phoneNumber"
                                phoneTypeName="phoneType"
                                onChange={this.updateRecipient}
                                error={dataErrors.phoneNumber}
                                updateError={this.updateDataErrors}
                                errorName="phoneNumber" />
                        </div>

                        {this.state.showSecondPhone && <div className="column-third">
                            <ValidatedPhoneInput
                                label="Second Phone"
                                phoneNumber={recipient.secondPhoneNumber}
                                phoneType={recipient.secondPhoneType}
                                phoneNumberName="secondPhoneNumber"
                                phoneTypeName="secondPhoneType"
                                onChange={this.updateRecipient}
                                error={dataErrors.secondPhoneNumber}
                                updateError={this.props.updateDataErrors}
                                errorName="secondPhoneNumber" />
                        </div>}

                        {this.state.showEmail && <div className="column-third">
                            <ValidatedInput input={<input type="text" />}
                                label="Email" name="email" value={recipient.email}
                                onChange={this.updateRecipient}
                                required={false} showRequiredError={false}
                                validated={true} showValidationError={true}
                                validate={this.validateEmail.bind(this, 'email')}
                                errorMessage="Invalid Email Address" />
                        </div>}
                    </div>
                    <div className="info-row flex-justify address-error-container">
                        <AddressAutocomplete
                            isIsraeli={recipient.isIsraeli}
                            required={true}
                            showRequiredError={submitAttempted || (this.props.recipient.incomplete && this.props.recipient.addressUsedForThirdParty && !recipient.address.street)}
                            address={recipient.address}
                            updateAddress={(address) => this.updateRecipient('address', address)}
                            updateError={this.updateDataErrors.bind(this, 'recipAddressError')}
                            addAddressLabel={(recipient.address.city || recipient.address.country || recipient.address.state
                                || recipient.address.street || recipient.address.streetNumber || recipient.address.zip)
                                && <span
                                    className="link-text small-text"
                                    onClick={() => {
                                        this.onChange('recipient', {
                                            ...recipient,
                                            address: {},
                                            previousAddress: { ...recipient.address }
                                        });
                                        if (!showPrevAddress) {
                                            this.onChange('showPrevAddress', true);
                                        }
                                    }}
                                    title="Set current address to previous"
                                >Add New Address</span>
                            }
                        />

                        <FadeOutErrorMessage show={this.props.recipient.incomplete && this.props.recipient.addressUsedForThirdParty && !recipient.address.street}
                            message="Address is used for associated application payments and is therefore required." />
                    </div>
                    {showPrevAddress && <div className="info-row flex-justify">
                        <AddressAutocomplete
                            title='Previous Address'
                            isIsraeli={recipient.isIsraeli}
                            showRequiredError={submitAttempted} required={false}
                            address={recipient.previousAddress}
                            updateAddress={(address) => this.updateRecipient('previousAddress', address)}
                            updateError={this.updateDataErrors.bind(this, 'recipPreviousAddressError')} />
                    </div>}
                    {(recipient.isIsraeli && recipient.applicantType === RecipientTypes.Individual) &&
                        <div className="info-row">
                            <ValidatedInput 
                            input={<NumberFormat placeholder="#########" format="#########"
                                onValueChange={(values) => this.updateRecipient('teudatZahut', values.value)} />}
                                label="Teudat Zehut" name="teudatZahut"
                                value={recipient.teudatZahut}
                                required={recipient.isIsraeli}
                                showRequiredError={submitAttempted} />
                        </div>}
                    {recipient.isIsraeli &&
                        <div className="info-row">
                            <BankAccountsTable
                                bankAccounts={recipient.bankAccounts}
                                submitAttempted={submitAttempted}
                                validateLength={this.validateLength.bind(this)}
                                updateBankAccounts={accounts => this.updateRecipient('bankAccounts', accounts)} />
                        </div>}
                        {!recipient.isIsraeli && recipient.applicantType === RecipientTypes.Organization &&
                        <div className="info-row">
                            <AchAccountsTable
                                bankAccounts={recipient.bankAccounts}
                                submitAttempted={submitAttempted}
                                validateLength={this.validateLength.bind(this)}
                                updateBankAccounts={accounts => this.updateRecipient('bankAccounts', accounts)} />
                        </div>}
                    <div className="column-third categories-row">
                        <ValidatedInput input={<AutocompleteMultiInput
                            showList={true}
                            options={recipient.applicantType === RecipientTypes.Individual ?
                                this.props.tags.categories.individualTags : this.props.tags.categories.organizationTags}
                            selection={recipient.tags}
                            update={this.updateRecipient} />}
                            label="Categories"
                            name="tags"
                            value={recipient.tags.length}
                            required={true}
                            showRequiredError={submitAttempted} />
                    </div>
                    <br />
                    <br />
                    {recipient.applicantType === RecipientTypes.Individual && <div className="info-row">
                        <div className="column-half">
                            <RadioGroup
                                title="Current Position"
                                name="orgEmploymentType"
                                value={recipient.orgEmploymentType}
                                options={this.props.tags.orgEmploymentTypes}
                                onChange={this.onChangeParticipant} />
                        </div>
                        <div className="column-half">
                            {recipient.orgEmploymentType === OrgEmploymentTypes.Other &&
                                <div className="no-margin-bottom-input">
                                    <ValidatedInput
                                        input={<input type="text" />}
                                        label="Specify"
                                        name="orgEmploymentPosition"
                                        value={recipient.orgEmploymentPosition}
                                        required={true}
                                        showRequiredError={submitAttempted && recipient.orgEmploymentPosition === ""}
                                        onChange={this.onChangeParticipant} />
                                </div>
                            }
                            {recipient.orgEmploymentType === OrgEmploymentTypes.Rebbi &&
                                <div className="flex">
                                    <div className="no-margin-bottom-input">
                                        <label htmlFor="" className="input-label">Grade</label>
                                        <ValidatedInput
                                            input={<Select
                                                options={[
                                                    { key: 0, value: "Pre-K" },
                                                    { key: 1, value: "1" },
                                                    { key: 2, value: "2" },
                                                    { key: 3, value: "3" },
                                                    { key: 4, value: "4" },
                                                    { key: 5, value: "5" },
                                                    { key: 6, value: "6" },
                                                    { key: 7, value: "7" },
                                                    { key: 8, value: "8" },
                                                    { key: 9, value: "9" },
                                                    { key: 10, value: "10" },
                                                    { key: 11, value: "11" },
                                                    { key: 12, value: "12" }
                                                ]}
                                                allowZero={true}
                                            />}
                                            name="orgEmploymentGrade"
                                            value={recipient.orgEmploymentGrade}
                                            onChange={this.onChangeParticipant}
                                            required={true}
                                            showRequiredError={submitAttempted && recipient.orgEmploymentGrade === ""}
                                        />
                                    </div>

                                    {(recipient.orgEmploymentGrade >= 9) &&
                                        <div className="ml-16">
                                            <RadioGroup
                                                title="Seder"
                                                error={submitAttempted && !recipient.orgEmploymentSeder}
                                                name="orgEmploymentSeder"
                                                value={recipient.orgEmploymentSeder}
                                                options={[
                                                    { key: 1, value: "1st" },
                                                    { key: 2, value: "2nd" }
                                                ]}
                                                onChange={this.onChangeParticipant} />
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    </div>}
                    <br />
                    <br />
                    <div className="info-row references-row">
                        <RecipientReferencesTable
                            references={recipient.references}
                            solicitors={recipient.solicitors}
                            roshYeshivas={recipient.roshYeshivas}
                            trustedPayee={recipient.trustedPayee}
                            updateRecipient={this.updateRecipient}
                            updateRecipientProps={props => this.onChange('recipient', {
                                ...recipient,
                                ...props
                            })}
                            setTrustedPayee={this.state.setTrustedPayee}
                            toggleSetTrustedPayee={this.onChange}
                            submitAttempted={submitAttempted} />
                    </div>
                    <div className="info-row pledges-row">
                        <ApplicantPledgesTable
                            applicantPledges={recipient.applicantPledges}
                            updateRecipient={this.updateRecipient}
                            updateRecipientProps={props => this.props.onChange('recipient', {
                                ...recipient,
                                ...props
                            })}
                            submitAttempted={submitAttempted}
                        />
                    </div>

                    {(submitAttempted && incompleteFields) &&
                        <IncompleteApplicationWarning
                            fields={incompleteFields}
                            allowIncomplete={allowIncomplete} />}

                    {this.props.updateRecipientDetails.loading ? <LoadingSpinner height="20" alignRight={true} /> :
                        <div className="details-card-footer clearfix text-right">
                            <button className="btn btn-accent" onClick={this.props.toggleEditMode}>Cancel</button>
                            <button className="btn btn-accent2" onClick={this.save}
                                disabled={Object.keys(dataErrors).some(k => dataErrors[k]) || isDisabled}>
                                Update
                            </button>

                            {isDisabled && disabledMessage && <span className="info-label">{disabledMessage}</span>}

                            <FadeOutErrorMessage show={this.state.error && !(this.props.updateRecipientDetails.message && this.props.updateRecipientDetails.message.indexOf('\n') > 0)}
                                message={this.props.updateRecipientDetails.message || 'Something went wrong and the recipient could not be updated. Please try again.'} />
                        </div>}

                    {(this.state.error && this.props.updateRecipientDetails.message && this.props.updateRecipientDetails.message.indexOf('\n') > 0) && <div className="text-right">
                        {this.props.updateRecipientDetails.message.split('\n').map((text, index) => <span className="error-label" key={index}>{text}</span>)}
                    </div>}
                </div>
            </div>
        )
    }
}


// WEBPACK FOOTER //
// ./src/components/recipientAccountsComponents/recipientAccountDetails/RecipientDetailsEditCard.jsx